/**
 * useSearchableList Hook
 *
 * Provides data fetching, pagination, and client-side filtering for SearchableListPanel.
 * Supports assets, risks (vulnerabilities), and techniques with unified interface.
 */

import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useDebounce } from 'use-debounce';

import { useMy } from '@/hooks';
import { MITRE_TECHNIQUES } from '@/sections/attacks/attackPaths/components/wizards/hooks/useTechniqueSearch';
import type {
  Asset,
  GraphQuery,
  NodeLabel,
  Operator,
  QueryFilter,
  Relationship,
  Risk,
} from '@/types';
import {
  isADAsset,
  resolveADDisplayName,
} from '@/utils/entities/adDisplayName.util';

/**
 * Entity types supported by the searchable list
 */
export type SearchableEntityType = 'asset' | 'risk' | 'technique';

/**
 * Unified item format for the list
 */
export interface SearchableListItem {
  /** Unique identifier (entity key) */
  key: string;
  /** Primary display text */
  name: string;
  /** Secondary metadata text */
  metadata?: string;
  /** Category/tactic for techniques (e.g., "Reconnaissance", "Execution") */
  category?: string;
  /** Optional icon indicator */
  hasIcon?: boolean;
  /** Raw status code (e.g., 'OH' for risks, 'A' for assets) */
  status?: string;
  /** Entity type for determining which variant map to use */
  entityType?: 'asset' | 'risk';
  /** Numeric priority for severity sorting (0=Critical, 10=High, etc.) */
  priority?: number;
  /** Original entity data for selection callback */
  originalData: Record<string, unknown>;
}

/**
 * Hook configuration options
 */
export interface UseSearchableListOptions {
  /** Entity type to fetch */
  type: SearchableEntityType;
  /** Current search/filter term */
  searchTerm: string;
  /** Items per page (default: 200) */
  pageSize?: number;
  /** Optional filter for assets by vulnerability key */
  vulnerabilityKey?: string;
  /**
   * Optional preselected item key for edit mode.
   * When provided, ensures this item is included in results even if
   * it would otherwise be outside the pagination window.
   */
  preselectedKey?: string;
  /**
   * Opt-in, server-side status-PREFIX allow-list (asset queries only).
   *
   * Each value is matched with `STARTS WITH`, so `'A'` covers the whole active
   * family (`A`/`AL`/`AP`/`AH`) — NOT an exact `=` match, which would silently
   * drop ActiveLow/ActivePassive/ActiveHigh assets. Emitted on `node.filters`,
   * so it ANDs with the search OR-block.
   *
   * Omit ⇒ no status filter is applied (the default for the other consumers).
   */
  assetStatuses?: string[];
  /**
   * Opt-in, server-side class allow-list (asset queries only).
   *
   * Matched via `IN`, so `['domain','ipv4','webapplication']` keeps only those
   * classes. Emitted on `node.filters` so it ANDs with other filters.
   *
   * Omit ⇒ no class filter (the default for most consumers).
   */
  assetClasses?: string[];
  /**
   * Opt-in, server-side fulltext search (asset queries only).
   *
   * When set, the search term is emitted on `node.search` (the Lucene-indexed
   * `assetSearchIndex` covering key/name/dns/identifier) instead of the default
   * `CONTAINS` OR-block — the same mechanism the global asset search bar uses.
   * Requires ≥2 chars (shorter terms emit no search, mirroring the search bar);
   * client-side re-filtering is skipped so the fulltext index is the sole
   * source of truth.
   *
   * Omit ⇒ default `CONTAINS` substring matching (the default for other
   * consumers).
   */
  fulltextSearch?: boolean;
}

/**
 * Hook return type
 */
export interface UseSearchableListReturn {
  /** Filtered items to display (after client-side search) */
  items: SearchableListItem[];
  /** All loaded items (before filtering) */
  allItems: SearchableListItem[];
  /** Whether initial data is loading */
  isLoading: boolean;
  /** Whether more pages are available */
  hasNextPage: boolean;
  /** Function to load next page */
  fetchNextPage: () => void;
  /** Whether currently fetching next page */
  isFetchingNextPage: boolean;
  /** Total count of loaded items */
  totalLoaded: number;
}

/**
 * Transform raw asset data to unified list item format
 */
function transformAsset(asset: Asset): SearchableListItem {
  // Extended type assertion for AD-specific properties
  const assetData = asset as Asset & {
    displayName?: string;
    displayname?: string;
    distinguishedname?: string;
    samaccountname?: string;
    name?: string;
    class?: string;
    domain?: string;
  };

  let identifier: string;

  // Use AD-aware display name resolution for AD objects
  if (isADAsset(asset.key)) {
    identifier = resolveADDisplayName({
      key: asset.key,
      identifier: asset.identifier,
      group: asset.group,
      class: assetData.class,
      name: assetData.name,
      displayName: assetData.displayName,
      displayname: assetData.displayname,
      samaccountname: assetData.samaccountname,
      distinguishedname: assetData.distinguishedname,
      domain: assetData.domain,
    });
  } else {
    // For non-AD assets: standard priority
    identifier =
      assetData.displayName ??
      assetData.displayname ??
      assetData.distinguishedname ??
      asset.identifier ??
      '';
  }

  return {
    key: asset.key ?? '',
    name: identifier,
    metadata: asset.group ?? undefined,
    hasIcon: asset.source === 'seed',
    status: asset.status,
    entityType: 'asset',
    originalData: asset as unknown as Record<string, unknown>,
  };
}

/**
 * Transform raw risk data to unified list item format
 */
function transformRisk(risk: Risk): SearchableListItem {
  return {
    key: risk.key ?? '',
    name: risk.name ?? '',
    metadata: risk.dns ?? undefined,
    status: risk.status,
    entityType: 'risk',
    priority: risk.priority,
    originalData: risk as unknown as Record<string, unknown>,
  };
}

/**
 * Transform raw technique data to unified list item format
 * (Techniques use static data, not API)
 */
function transformTechnique(technique: {
  id: string;
  name: string;
  tactic?: string;
  uniqueKey?: string;
}): SearchableListItem {
  return {
    key: technique.uniqueKey ?? technique.id,
    name: technique.name,
    metadata: technique.id,
    category: technique.tactic,
    originalData: technique as unknown as Record<string, unknown>,
  };
}

/**
 * Hook for fetching and filtering searchable list data.
 *
 * @param options - Configuration including entity type and search term
 * @returns List data with filtering and pagination support
 */
export function useSearchableList(
  options: UseSearchableListOptions
): UseSearchableListReturn {
  const {
    type,
    searchTerm,
    pageSize = 200,
    vulnerabilityKey,
    preselectedKey,
    assetStatuses,
    assetClasses,
    fulltextSearch,
  } = options;

  // Debounce search for server-side query (0ms when clearing, 300ms when typing)
  const [debouncedSearch] = useDebounce(
    searchTerm,
    searchTerm === '' ? 0 : 300
  );

  // Fulltext (node.search) is asset-only and opt-in. When on, the Lucene index
  // does the matching server-side, so the CONTAINS OR-block and the client-side
  // re-filter are both bypassed.
  const useFulltext = type === 'asset' && Boolean(fulltextSearch);

  // Determine if we should use API (techniques use static data)
  const useApi = type !== 'technique';

  // Only filter by vulnerability if key is provided AND it's not a custom vulnerability
  const hasVulnerabilityFilter =
    type === 'asset' &&
    Boolean(vulnerabilityKey) &&
    !vulnerabilityKey?.startsWith('__custom__');

  // Build graph query for assets and risks with server-side search
  const graphQuery = useMemo((): GraphQuery | undefined => {
    if (!useApi) return undefined;

    const nodeLabel: NodeLabel = type === 'asset' ? 'Asset' : 'Risk';
    const alias = type === 'risk' ? 'risk' : 'asset';

    // Build global filters for server-side search
    const globalFilters: QueryFilter[] = [];

    // Fulltext path (asset-only, opt-in): emit node.search instead of a
    // CONTAINS OR-block. Mirrors the global asset search bar's min-2-char gate.
    const searchParam =
      useFulltext && debouncedSearch.length >= 2 ? debouncedSearch : undefined;

    // Build search filter conditions (skipped on the fulltext path)
    const searchConditions: QueryFilter[] =
      !useFulltext && debouncedSearch
        ? type === 'risk'
          ? [
              {
                field: 'key',
                operator: 'CONTAINS' as Operator,
                value: debouncedSearch,
                alias: 'risk',
              },
              {
                field: 'name',
                operator: 'CONTAINS' as Operator,
                value: debouncedSearch,
                alias: 'risk',
              },
              {
                field: 'title',
                operator: 'CONTAINS' as Operator,
                value: debouncedSearch,
                alias: 'vuln',
              },
            ]
          : [
              {
                field: 'key',
                operator: 'CONTAINS' as Operator,
                value: debouncedSearch,
                alias: 'asset',
              },
              {
                field: 'identifier',
                operator: 'CONTAINS' as Operator,
                value: debouncedSearch,
                alias: 'asset',
              },
              {
                field: 'group',
                operator: 'CONTAINS' as Operator,
                value: debouncedSearch,
                alias: 'asset',
              },
              {
                field: 'name',
                operator: 'CONTAINS' as Operator,
                value: debouncedSearch,
                alias: 'asset',
              },
            ]
        : [];

    // Add preselected key filter ONLY when there are search conditions.
    // This ensures the selected item appears in search results when the user searches,
    // even if it doesn't match the search term. When no search term is entered,
    // don't filter at all - return normal paginated results so user can browse all items.
    if (
      preselectedKey &&
      !preselectedKey.startsWith('__custom__') &&
      searchConditions.length > 0
    ) {
      searchConditions.push({
        field: 'key',
        operator: '=' as Operator,
        value: preselectedKey,
        alias,
      });
    }

    // Combine search conditions with OR operator
    if (searchConditions.length > 0) {
      globalFilters.push({
        field: '',
        operator: 'OR',
        value: searchConditions,
      });
    }

    // Build relationships array based on entity type
    const relationships = [
      // Add INSTANCE_OF relationship for risks to enable Vulnerability.title search
      ...(type === 'risk'
        ? [
            {
              label: 'INSTANCE_OF' as Relationship,
              target: {
                alias: 'vuln',
                labels: ['Vulnerability' as NodeLabel],
              },
              optional: true,
              select_one: true,
            },
          ]
        : []),
      // Add HAS_VULNERABILITY relationship for assets when vulnerability is selected
      ...(hasVulnerabilityFilter
        ? [
            {
              label: 'HAS_VULNERABILITY' as Relationship,
              target: {
                labels: ['Risk' as NodeLabel],
                filters: [
                  {
                    field: 'key',
                    operator: '=' as Operator,
                    value: vulnerabilityKey ?? '',
                  },
                ],
              },
            },
          ]
        : []),
    ];

    // Opt-in, server-side status-PREFIX allow-list (asset queries only).
    // Matched via STARTS WITH so 'A' covers the active family (A/AL/AP/AH).
    // Emitted on node.filters so it ANDs with the search OR-block above.
    const statusFilter: QueryFilter | undefined =
      type === 'asset' && assetStatuses?.length
        ? assetStatuses.length === 1
          ? {
              field: 'status',
              operator: 'STARTS WITH' as Operator,
              value: assetStatuses[0],
              alias,
            }
          : {
              field: '',
              operator: 'OR' as Operator,
              value: assetStatuses.map(s => ({
                field: 'status',
                operator: 'STARTS WITH' as Operator,
                value: s,
                alias,
              })),
            }
        : undefined;

    // Opt-in, server-side class allow-list (asset queries only).
    // Matched via IN so only specified classes are returned.
    const classFilter: QueryFilter | undefined =
      type === 'asset' && assetClasses?.length
        ? {
            field: 'class',
            operator: 'IN' as Operator,
            value: [assetClasses],
            alias,
          }
        : undefined;

    return {
      node: {
        alias: type === 'risk' ? 'risk' : 'asset',
        labels: [nodeLabel],
        // Fulltext search via the assetSearchIndex (key/name/dns/identifier),
        // ANDs with the status/class filters below. Asset-only, opt-in.
        ...(searchParam ? { search: searchParam } : {}),
        // Only add relationships if there are any
        ...(relationships.length > 0 ? { relationships } : {}),
        // Status allow-list (asset-only, opt-in) — ANDs with the search OR-block.
        ...(statusFilter || classFilter
          ? {
              filters: [
                ...(statusFilter ? [statusFilter] : []),
                ...(classFilter ? [classFilter] : []),
              ],
            }
          : {}),
      },
      filters: globalFilters.length > 0 ? globalFilters : undefined,
      limit: pageSize,
    };
  }, [
    type,
    useApi,
    debouncedSearch,
    hasVulnerabilityFilter,
    vulnerabilityKey,
    pageSize,
    preselectedKey,
    assetStatuses,
    assetClasses,
    useFulltext,
  ]);

  // Fetch data from API (assets and risks)
  const {
    data: rawData = [],
    isLoading,
    fetchNextPage: apiFetchNextPage,
    hasNextPage = false,
    isFetchingNextPage,
  } = useMy(
    {
      resource: type === 'asset' ? 'asset' : 'risk',
      ...(type === 'risk' ? { tree: true } : {}), // Required for INSTANCE_OF relationship
      query: graphQuery,
    },
    {
      enabled: useApi,
      placeholderData: keepPreviousData, // Prevent UI flash during search
    }
  );

  // Transform raw data to unified format
  const allItems = useMemo(() => {
    if (type === 'technique') {
      // Techniques use static MITRE data
      return MITRE_TECHNIQUES.map(transformTechnique).sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
    }

    let dataToTransform: Asset[] | Risk[];

    if (type === 'risk') {
      // Extract risks from tree structure when using relationships
      // Tree response structure: [{ node: { result: { risks: [Risk] } } }]
      dataToTransform = (rawData as unknown[])
        .map((tree: unknown) => {
          const treeNode = tree as {
            node?: { result?: { risks?: Risk[] } };
          };
          return treeNode?.node?.result?.risks?.[0];
        })
        .filter((risk): risk is Risk => Boolean(risk));
    } else {
      // Assets use flat response (no tree mode)
      dataToTransform = rawData as Asset[];
    }

    const transformed =
      type === 'asset'
        ? (dataToTransform as Asset[]).map(transformAsset)
        : (dataToTransform as Risk[]).map(transformRisk);

    // Sort alphabetically by name
    return transformed.sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );
  }, [rawData, type]);

  // Client-side filtering based on search term.
  // On the fulltext path the server-side Lucene index is the sole source of
  // truth — re-filtering here on name/metadata only would drop rows matched on
  // key/dns/identifier, so skip it entirely.
  const items = useMemo(() => {
    if (useFulltext || !searchTerm.trim()) {
      return allItems;
    }

    const lowerSearch = searchTerm.toLowerCase();
    return allItems.filter(
      item =>
        item.name.toLowerCase().includes(lowerSearch) ||
        item.metadata?.toLowerCase().includes(lowerSearch)
    );
  }, [allItems, searchTerm, useFulltext]);

  // Detect pending debounce - search term changed but debounced value hasn't caught up
  const isSearchPending = searchTerm !== debouncedSearch;

  const fetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      apiFetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, apiFetchNextPage]);

  return {
    items,
    allItems,
    isLoading: useApi ? isLoading || isSearchPending : false,
    hasNextPage: useApi ? hasNextPage : false,
    fetchNextPage,
    isFetchingNextPage: useApi ? isFetchingNextPage : false,
    totalLoaded: allItems.length,
  };
}
